import { ref } from 'vue'
import { getClipboardText, dispatchCopyEventFrom } from '@/util/copypaste_helpers'

// WCAG 2.2.1 (Timing Adjustable) forbids closing a confirmation purely on a
// clock: the user must be able to extend or dismiss it themselves. Pointer
// leaving or focus leaving the trigger is what dismisses it, but only once
// it's been visible for at least `delay` — moving on right after clicking
// (a very common flow) must not make it disappear any faster than it did
// before this fix. A fixed fallback still guards input modes that never
// fire mouseleave/blur (e.g. some touch flows) — Technique G180 asks for at
// least 10x the nominal delay in that case.
const FALLBACK_DELAY_MULTIPLIER = 10

export function useCopyText() {
  const isTooltipOpen = ref(false)
  let hideTimeoutId: ReturnType<typeof setTimeout> | null = null
  let dismissTarget: HTMLElement | null = null
  let minVisibleUntil = 0

  const hideTooltip = () => {
    isTooltipOpen.value = false
    if (dismissTarget) {
      dismissTarget.removeEventListener('mouseleave', onDisengage)
      dismissTarget.removeEventListener('blur', onDisengage)
      dismissTarget = null
    }
    if (hideTimeoutId) {
      clearTimeout(hideTimeoutId)
      hideTimeoutId = null
    }
  }

  const onDisengage = () => {
    const remaining = minVisibleUntil - Date.now()
    if (remaining <= 0) {
      hideTooltip()
      return
    }
    // Still inside the minimum-visible window: don't close now, just stop
    // waiting on the (much longer) fallback and close right at the minimum.
    if (hideTimeoutId) clearTimeout(hideTimeoutId)
    hideTimeoutId = setTimeout(hideTooltip, remaining)
  }

  // Shows the tooltip and lets the user decide how long it stays visible,
  // down to the minimum guaranteed by `delay`.
  const showTooltipUntilDismissed = (triggerElement?: HTMLElement | null, delay = 2000) => {
    hideTooltip()
    isTooltipOpen.value = true
    minVisibleUntil = Date.now() + delay
    if (triggerElement) {
      dismissTarget = triggerElement
      dismissTarget.addEventListener('mouseleave', onDisengage)
      dismissTarget.addEventListener('blur', onDisengage)
    }
    hideTimeoutId = setTimeout(hideTooltip, delay * FALLBACK_DELAY_MULTIPLIER)
  }

  const copyText = async (
    text: string,
    event?: MouseEvent,
    options?: {
      autoHideTooltip?: boolean
      autoHideDelay?: number
      onCopied?: () => void
    }
  ) => {
    const textToCopy = getClipboardText(text)
    const buttonElement = event?.currentTarget as HTMLElement | undefined

    if (buttonElement) {
      dispatchCopyEventFrom(buttonElement, true, textToCopy)
    }

    await navigator.clipboard.writeText(textToCopy)

    if (options?.autoHideTooltip) {
      showTooltipUntilDismissed(buttonElement, options.autoHideDelay || 2000)
    } else {
      isTooltipOpen.value = true
    }

    if (options?.onCopied) {
      options.onCopied()
    }
  }

  return {
    isTooltipOpen,
    copyText,
    showTooltipUntilDismissed,
    hideTooltip
  }
}
